import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { Switch } from "@/components/Switch";
import { ColorPickerModal } from "@/components/ColorPickerModal";
import { useToast } from "@/context/ToastContext";
import { taskStatusesApi } from "@/lib/api";
import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { PageHeader } from "@/components/PageHeader";

export interface TaskStatusItem {
    id: number | string;
    name: string;
    tag: string;
    color: string;
    order: number;
    is_default?: boolean;
    is_hidden?: boolean;
}

export interface TaskStatusesSettingsProps {
    onStatusesUpdated?: () => void;
}

export const TaskStatusesSettings = ({
    onStatusesUpdated,
}: TaskStatusesSettingsProps) => {
    const { t } = useTranslation("global");
    const { showSuccess, showError } = useToast();
    const [originalStatuses, setOriginalStatuses] = useState<TaskStatusItem[]>(
        []
    );
    const [statuses, setStatuses] = useState<TaskStatusItem[]>([]);
    const [deletedIds, setDeletedIds] = useState<(number | string)[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [isDiscardModalOpen, setIsDiscardModalOpen] =
        useState<boolean>(false);

    const isDirty =
        deletedIds.length > 0 ||
        JSON.stringify(statuses) !== JSON.stringify(originalStatuses);

    useEffect(() => {
        loadStatuses(true);
    }, []);

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = "";
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () =>
            window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [isDirty]);

    const loadStatuses = async (showLoading: boolean = true): Promise<void> => {
        if (showLoading) {
            setLoading(true);
        }
        try {
            const res = await taskStatusesApi.list();
            if (res.result) {
                setStatuses(res.data);
                setOriginalStatuses(JSON.parse(JSON.stringify(res.data)));
            }
        } catch (error) {
            console.error("Error loading statuses:", error);
            showError(t("tasks.error_loading"));
        } finally {
            if (showLoading) {
                setLoading(false);
            }
        }
    };

    const handleUpdateStatus = (
        id: number | string,
        field: keyof TaskStatusItem,
        value: any
    ): void => {
        setStatuses((prev) =>
            prev.map((s) => (s.id === id ? { ...s, [field]: value } : s))
        );
    };

    const handleDeleteStatus = (id: number | string): void => {
        setDeletedIds((prev) => [...prev, id]);
    };

    const handleRestoreStatus = (id: number | string): void => {
        setDeletedIds((prev) => prev.filter((item) => item !== id));
    };

    const handleDiscardChanges = (): void => {
        setStatuses(JSON.parse(JSON.stringify(originalStatuses)));
        setDeletedIds([]);
        setIsDiscardModalOpen(false);
    };

    const handleCreateStatus = (): void => {
        const tempId = `temp_${Date.now()}`;
        const activeStatuses = statuses.filter(
            (s) => !deletedIds.includes(s.id)
        );
        const existingTags = new Set(
            activeStatuses.map((s) => (s.tag || "").trim().toLowerCase())
        );
        let newTag = "new_column";
        let count = 1;
        while (existingTags.has(newTag)) {
            newTag = `new_column_${count++}`;
        }

        const newStatus: TaskStatusItem = {
            id: tempId,
            name: "New Column",
            tag: newTag,
            color: "#94a3b8",
            order: statuses.length + 1,
            is_default: false,
            is_hidden: false,
        };
        setStatuses((prev) => [...prev, newStatus]);
    };

    const handleDragEnd = (result: any): void => {
        if (!result.destination) {
            return;
        }

        const items = Array.from(statuses);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        // Update order field based on new positions
        const reordered = items.map((item, index) => ({
            ...item,
            order: index + 1,
        }));
        setStatuses(reordered);
    };

    const handleSave = async (): Promise<void> => {
        const activeStatuses = statuses.filter(
            (s) => !deletedIds.includes(s.id)
        );

        // Tag required validation for active statuses
        const hasEmptyTag = activeStatuses.some((s) => !s.tag || !s.tag.trim());
        if (hasEmptyTag) {
            showError(t("tasks.manage_columns.error_tag_required"));
            return;
        }

        // Tag unique validation for active statuses
        const normalizedTags = activeStatuses.map((s) =>
            s.tag.trim().toLowerCase()
        );
        const uniqueTags = new Set(normalizedTags);
        if (uniqueTags.size !== normalizedTags.length) {
            showError(t("tasks.manage_columns.error_tag_unique"));
            return;
        }

        // Name required validation for active statuses
        const hasEmptyName = activeStatuses.some(
            (s) => !s.name || !s.name.trim()
        );
        if (hasEmptyName) {
            showError(t("tasks.manage_columns.error_name_required"));
            return;
        }

        setSaving(true);
        try {
            // 1. Delete removed statuses from database (only existing ones with number ID)
            for (const id of deletedIds) {
                if (typeof id === "number") {
                    const res = await taskStatusesApi.delete(id);
                    if (res?.result === 0 || res?.status === 400) {
                        showError(
                            res.message ||
                                t("tasks.manage_columns.error_delete")
                        );
                        setSaving(false);
                        return;
                    }
                }
            }

            // 2. Create new active statuses
            const created = activeStatuses.filter(
                (s) => typeof s.id === "string" && s.id.startsWith("temp_")
            );
            const idMap: Record<string, number> = {};
            for (const status of created) {
                const { id, ...data } = status;
                const res = await taskStatusesApi.create(data);
                if (res.result) {
                    idMap[id as string] = res.data.id;
                }
            }

            // 3. Update modified active statuses
            const updated = activeStatuses.filter(
                (s) =>
                    typeof s.id === "number" &&
                    JSON.stringify(s) !==
                        JSON.stringify(
                            originalStatuses.find((o) => o.id === s.id)
                        )
            );
            for (const status of updated) {
                const { id, ...data } = status;
                await taskStatusesApi.update(id as number, data);
            }

            // 4. Reorder active statuses
            const finalStatusesList = activeStatuses.map((s, idx) => ({
                id: typeof s.id === "string" ? idMap[s.id] : (s.id as number),
                order: idx + 1,
            }));
            await taskStatusesApi.reorder({ statuses: finalStatusesList });

            showSuccess(t("common.saved"));

            if (onStatusesUpdated) {
                onStatusesUpdated();
            }

            // Reload silently to synchronize state with server without unmounting UI
            setDeletedIds([]);
            await loadStatuses(false);
        } catch (error: any) {
            console.error("Error saving statuses:", error);
            const msg = error?.message || t("tasks.error_loading");
            showError(msg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <PageHeader
                title={
                    <span className="flex items-center gap-2">
                        <Icon
                            value="layoutTemplate"
                            size={20}
                            className="text-primary"
                        />
                        {t("tasks.manage_columns.title")}
                    </span>
                }
                subtitle={t("tasks.manage_columns.subtitle")}
                action={{
                    label: t("tasks.manage_columns.add_column"),
                    icon: "plus",
                    onClick: handleCreateStatus,
                }}
            />

            {/* Warning banner explaining batch saving */}
            <div className="my-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/60 text-amber-800 dark:text-amber-300 p-3.5 rounded-xl text-xs sm:text-sm flex items-start sm:items-center gap-2.5">
                <Icon
                    name="alertTriangle"
                    className="w-5 h-5 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5 sm:mt-0"
                />
                <span className="leading-snug">
                    {t("tasks.manage_columns.batch_warning")}
                </span>
            </div>

            <div className="box mt-4">
                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-primary"></div>
                    </div>
                ) : (
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="manage-statuses">
                            {(provided) => (
                                <div
                                    className="space-y-3"
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                >
                                    {statuses.map((status, index) => {
                                        const isDeleted = deletedIds.includes(
                                            status.id
                                        );
                                        const trimmedTag = (status.tag || "")
                                            .trim()
                                            .toLowerCase();
                                        const isTagEmpty =
                                            !isDeleted &&
                                            (!status.tag || !status.tag.trim());
                                        const isTagDuplicate = Boolean(
                                            !isDeleted &&
                                                trimmedTag &&
                                                statuses.filter(
                                                    (s) =>
                                                        !deletedIds.includes(
                                                            s.id
                                                        ) &&
                                                        (s.tag || "")
                                                            .trim()
                                                            .toLowerCase() ===
                                                            trimmedTag
                                                ).length > 1
                                        );
                                        const isNameEmpty =
                                            !isDeleted &&
                                            (!status.name ||
                                                !status.name.trim());

                                        return (
                                            <Draggable
                                                key={status.id.toString()}
                                                draggableId={status.id.toString()}
                                                index={index}
                                                isDragDisabled={
                                                    isDeleted || saving
                                                }
                                            >
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        className={`group relative flex items-center gap-3 p-3 rounded-xl border transition-all ${
                                                            isDeleted
                                                                ? "border-dashed border-rose-300/80 dark:border-rose-900/60 bg-rose-50/10 dark:bg-rose-950/10"
                                                                : snapshot.isDragging
                                                                  ? "border-primary ring-2 ring-primary/20 shadow-md scale-[1.01] bg-white dark:bg-zinc-800"
                                                                  : "border-slate-200/80 dark:border-zinc-700/60 hover:border-primary/40 bg-slate-50/70 dark:bg-zinc-800/40"
                                                        }`}
                                                    >
                                                        {/* Inner row content - legible but disabled when marked for deletion */}
                                                        <div
                                                            className={`flex items-center gap-3 flex-1 min-w-0 transition-all ${
                                                                isDeleted
                                                                    ? "opacity-45 pointer-events-none select-none"
                                                                    : ""
                                                            }`}
                                                        >
                                                            <div
                                                                {...provided.dragHandleProps}
                                                                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 cursor-grab active:cursor-grabbing p-1 shrink-0"
                                                            >
                                                                <Icon
                                                                    name="gripVertical"
                                                                    className="w-5 h-5"
                                                                />
                                                            </div>

                                                            <ColorPickerModal
                                                                value={
                                                                    status.color ||
                                                                    "#cbd5e1"
                                                                }
                                                                onChange={(
                                                                    hex
                                                                ) =>
                                                                    handleUpdateStatus(
                                                                        status.id,
                                                                        "color",
                                                                        hex
                                                                    )
                                                                }
                                                            />

                                                            {/* Tag Input with Required/Unique indicators */}
                                                            <div className="flex-1 min-w-0">
                                                                <input
                                                                    type="text"
                                                                    value={
                                                                        status.tag ||
                                                                        ""
                                                                    }
                                                                    placeholder={t(
                                                                        "tasks.manage_columns.tag_placeholder"
                                                                    )}
                                                                    title={
                                                                        isTagEmpty
                                                                            ? t(
                                                                                  "tasks.manage_columns.tag_required"
                                                                              )
                                                                            : isTagDuplicate
                                                                              ? t(
                                                                                    "tasks.manage_columns.tag_unique"
                                                                                )
                                                                              : t(
                                                                                    "tasks.manage_columns.tag_title"
                                                                                )
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) => {
                                                                        handleUpdateStatus(
                                                                            status.id,
                                                                            "tag",
                                                                            e
                                                                                .target
                                                                                .value
                                                                        );
                                                                    }}
                                                                    className={`w-full bg-white dark:bg-zinc-900 border ${
                                                                        isTagEmpty ||
                                                                        isTagDuplicate
                                                                            ? "border-rose-400 dark:border-rose-500 ring-1 ring-rose-400/40 bg-rose-50/20"
                                                                            : "border-slate-200 dark:border-zinc-700 focus:border-primary focus:ring-primary/20"
                                                                    } rounded-lg px-3 py-1.5 text-sm font-medium text-slate-800 dark:text-zinc-100 focus:ring-2 outline-none transition-all`}
                                                                />
                                                                {(isTagEmpty ||
                                                                    isTagDuplicate) && (
                                                                    <span className="block text-[10px] text-rose-500 font-medium mt-0.5 ml-1">
                                                                        {isTagEmpty
                                                                            ? t(
                                                                                  "tasks.manage_columns.tag_required"
                                                                              )
                                                                            : t(
                                                                                  "tasks.manage_columns.tag_unique"
                                                                              )}
                                                                    </span>
                                                                )}
                                                            </div>

                                                            {/* Name Input with Required indicator */}
                                                            <div className="flex-1 min-w-0">
                                                                <input
                                                                    type="text"
                                                                    value={
                                                                        status.name ||
                                                                        ""
                                                                    }
                                                                    placeholder={t(
                                                                        "tasks.manage_columns.name_placeholder"
                                                                    )}
                                                                    title={
                                                                        isNameEmpty
                                                                            ? t(
                                                                                  "tasks.manage_columns.name_required"
                                                                              )
                                                                            : t(
                                                                                  "tasks.manage_columns.name_title"
                                                                              )
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) => {
                                                                        handleUpdateStatus(
                                                                            status.id,
                                                                            "name",
                                                                            e
                                                                                .target
                                                                                .value
                                                                        );
                                                                    }}
                                                                    className={`w-full bg-white dark:bg-zinc-900 border ${
                                                                        isNameEmpty
                                                                            ? "border-rose-400 dark:border-rose-500 ring-1 ring-rose-400/40 bg-rose-50/20"
                                                                            : "border-slate-200 dark:border-zinc-700 focus:border-primary focus:ring-primary/20"
                                                                    } rounded-lg px-3 py-1.5 text-sm font-medium text-slate-800 dark:text-zinc-100 focus:ring-2 outline-none transition-all`}
                                                                />
                                                                {isNameEmpty && (
                                                                    <span className="block text-[10px] text-rose-500 font-medium mt-0.5 ml-1">
                                                                        {t(
                                                                            "tasks.manage_columns.name_required"
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </div>

                                                            <div className="shrink-0">
                                                                <Switch
                                                                    checked={
                                                                        !!status.is_hidden
                                                                    }
                                                                    onChange={(
                                                                        checked
                                                                    ) =>
                                                                        handleUpdateStatus(
                                                                            status.id,
                                                                            "is_hidden",
                                                                            checked
                                                                        )
                                                                    }
                                                                    position="left"
                                                                    label={t(
                                                                        "tasks.manage_columns.hidden"
                                                                    )}
                                                                    labelClassName="text-slate-500 dark:text-zinc-400 font-medium text-xs"
                                                                />
                                                            </div>

                                                            <Button
                                                                variant="danger"
                                                                onClick={() =>
                                                                    handleDeleteStatus(
                                                                        status.id
                                                                    )
                                                                }
                                                                tooltip={t(
                                                                    "tasks.manage_columns.delete"
                                                                )}
                                                                icon={
                                                                    <Icon name="trash" />
                                                                }
                                                                onlyicon
                                                            />
                                                        </div>

                                                        {/* Dark hover backdrop with centered text restore action */}
                                                        {isDeleted && (
                                                            <div
                                                                onClick={() =>
                                                                    handleRestoreStatus(
                                                                        status.id
                                                                    )
                                                                }
                                                                className="absolute inset-0 bg-slate-900/60 dark:bg-zinc-950/70 backdrop-blur-[2px] rounded-xl flex items-center justify-center gap-2 text-white opacity-0 group-hover:opacity-100 cursor-pointer transition-all duration-200 z-20 p-2 shadow-md"
                                                                title={t(
                                                                    "tasks.manage_columns.restore"
                                                                )}
                                                            >
                                                                <Icon
                                                                    name="rotateCcw"
                                                                    className="w-4 h-4 text-white"
                                                                />
                                                                <span className="text-xs sm:text-sm font-semibold text-white tracking-wide cursor-pointer select-none">
                                                                    {t(
                                                                        "tasks.manage_columns.restore"
                                                                    )}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </Draggable>
                                        );
                                    })}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                )}

                {/* Action buttons underneath the statuses */}
                <div className="mt-6 flex items-center justify-center gap-3">
                    {isDirty && (
                        <Button
                            variant="secondary"
                            onClick={() => setIsDiscardModalOpen(true)}
                            disabled={saving}
                            icon="x"
                        >
                            {t("tasks.manage_columns.discard")}
                        </Button>
                    )}
                    <Button
                        variant="primary"
                        onClick={handleSave}
                        disabled={saving || !isDirty}
                        icon={saving ? "loader" : "save"}
                    >
                        {t("common.save")}
                    </Button>
                </div>
            </div>

            {/* Discard Changes Modal */}
            <Modal
                isOpen={isDiscardModalOpen}
                onClose={() => setIsDiscardModalOpen(false)}
                title={t("tasks.manage_columns.confirm_discard_title")}
                submitVariant="danger"
                submitIcon={<Icon name="trash" size={16} />}
                onSubmit={handleDiscardChanges}
                submitLabel={t("tasks.manage_columns.discard")}
                cancelLabel={t("modal.cancel")}
                hideFullscreen
                maxWidth="sm"
            >
                <div className="py-2 text-center text-sm text-slate-700 dark:text-zinc-300">
                    {t("tasks.manage_columns.confirm_discard_msg")}
                </div>
            </Modal>
        </div>
    );
};

export default TaskStatusesSettings;
